import { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { useTranslations } from "use-intl";
import { AuthProvider, useAuth } from "./auth";
import { DisclosurePage } from "./components/DisclosurePage";
import { LoginPage } from "./components/LoginPage";
import { McpAccessPage } from "./components/McpAccessPage";
import { PublicFlowTraceAnalytics } from "./components/PublicFlowTraceAnalytics";
import { reportFlowTraceFrontendIncident } from "./lib/frontend-incidents";

type SessionDetailComponent =
  typeof import("./components/SessionDetail")["SessionDetailPage"];

const DemoPage = lazy(() =>
  import("./components/DemoPage").then(({ DemoPage }) => ({ default: DemoPage })),
);

const RouteLoading = () => {
  const t = useTranslations("app");
  return <div className="full-page-status">{t("loading")}</div>;
};

const ProtectedRoutes = () => {
  const t = useTranslations("app");
  const { status } = useAuth();
  if (status === "loading") {
    return <div className="full-page-status">{t("loading")}</div>;
  }
  if (status !== "authorized") return <Navigate replace to="/login" />;
  return <Outlet />;
};

const AuthRoutes = () => (
  <AuthProvider>
    <Outlet />
  </AuthProvider>
);

const LazySessionDetailRoute = () => {
  const t = useTranslations("detail");
  const [retryKey, setRetryKey] = useState(0);
  const [component, setComponent] =
    useState<SessionDetailComponent | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setComponent(null);
    setLoadError(false);
    import("./components/SessionDetail")
      .then((module) => {
        if (!cancelled) setComponent(() => module.SessionDetailPage);
      })
      .catch((error) => {
        if (!cancelled) {
          reportFlowTraceFrontendIncident({
            detail:
              error instanceof Error
                ? error.stack || error.message
                : "Session detail module failed to load",
            route: window.location.pathname,
            summary: "Session detail module failed to load",
          });
          setLoadError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  if (loadError) {
    return (
      <div className="full-page-status">
        <div className="route-load-error">
          <p>{t("loadError")}</p>
          <button
            className="quiet-button"
            onClick={() => setRetryKey((current) => current + 1)}
            type="button"
          >
            {t("retry")}
          </button>
        </div>
      </div>
    );
  }

  if (!component) {
    return <div className="full-page-status">{t("loading")}</div>;
  }

  const Component = component;
  return <Component />;
};

const DocumentTitle = () => {
  const t = useTranslations("meta");
  const { pathname } = useLocation();
  let title = t("dashboardTitle");
  if (pathname === "/login") title = t("loginTitle");
  if (pathname === "/demo") title = t("demoTitle");
  if (pathname.startsWith("/disclosures/")) title = t("disclosureTitle");
  if (pathname.startsWith("/sessions/")) title = t("sessionTitle");

  useEffect(() => {
    document.title = title;
  }, [title]);

  return null;
};

export const App = () => (
  <BrowserRouter>
    <DocumentTitle />
    <PublicFlowTraceAnalytics />
    <Routes>
      <Route path="/disclosures/:appId" element={<DisclosurePage />} />
      <Route element={<AuthRoutes />}>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedRoutes />}>
          <Route path="/" element={<LazySessionDetailRoute />} />
          <Route
            path="/projects/:appId"
            element={<LazySessionDetailRoute />}
          />
          <Route
            path="/demo"
            element={
              <Suspense fallback={<RouteLoading />}>
                <DemoPage />
              </Suspense>
            }
          />
          <Route path="/mcp-access" element={<McpAccessPage />} />
          <Route
            path="/sessions/:appId/:sessionId"
            element={<LazySessionDetailRoute />}
          />
        </Route>
      </Route>
    </Routes>
  </BrowserRouter>
);
